import { log } from '../services/production-logger';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  updateProfile,
  User,
  UserCredential,
  getAuth,
  onAuthStateChanged,
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  getFirestore,
  collection,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { UserProfile } from '../types/user';
import { auth, firestore } from './firebase';

/**
 * Register a new user with email and password
 * @param email User's email
 * @param password User's password
 * @param displayName User's display name
 * @returns UserCredential
 */
export const registerUser = async (
  email: string,
  password: string,
  displayName: string,
): Promise<UserCredential> => {
  try {
    log.debug(`Attempting to register user: ${email}`);
    // Direct usage of auth and firestore instances
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const userId = userCredential.user.uid;
    log.debug(`User registered successfully: ${userId}`);

    // Update user profile with display name
    await updateProfile(userCredential.user, { displayName });
    log.debug(`User profile updated with display name: ${displayName}`);
    
    // Create default organization
    const orgData = {
      name: `${displayName}'s Organization`,
      ownerId: userId,
      memberIds: [userId],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: userId,
    };

    // Add organization to Firestore
    const orgRef = doc(collection(firestore, 'organizations'));
    const organizationId = orgRef.id;
    await setDoc(orgRef, orgData);
    log.debug(`Created default organization: ${organizationId}`);

    // Create membership linking user to organization
    const membershipRef = doc(collection(firestore, 'memberships'));
    await setDoc(membershipRef, {
      userId: userId,
      organizationId: organizationId,
      role: 'owner',
      isPrimary: true,
      createdAt: serverTimestamp(),
    });
    log.debug(`Created primary membership for user in organization: ${organizationId}`);

    // Create user document in Firestore
    await setDoc(doc(firestore, 'users', userId), {
      email,
      displayName,
      createdAt: new Date().toISOString(),
      role: 'user', // default role
      organizationId: organizationId, // Link to the new organization
    });
    log.debug(`User document created in Firestore: ${userId}`);

    // Store auth data in AsyncStorage as a backup
    try {
      await AsyncStorage.setItem(
        'authUser',
        JSON.stringify({
          uid: userId,
          email: userCredential.user.email,
          displayName,
        }),
      );
      log.debug('Auth data stored in AsyncStorage');
    } catch (storageError) {
      log.warn('Failed to store auth data in AsyncStorage:', storageError);
      // Continue with registration process even if AsyncStorage fails
    }

    return userCredential;
  } catch (error) {
    log.error('Error registering user:', error);
    throw error;
  }
};

/**
 * Sign in a user with email and password
 * @param email User's email
 * @param password User's password
 * @returns UserCredential
 */
export const signInUser = async (email: string, password: string): Promise<UserCredential> => {
  try {
    log.debug(`Attempting to sign in user: ${email}`);
    // Direct usage of auth instance
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    log.debug(`User signed in successfully: ${userCredential.user.uid}`);

    // Store auth data in AsyncStorage as a backup
    try {
      await AsyncStorage.setItem(
        'authUser',
        JSON.stringify({
          uid: userCredential.user.uid,
          email: userCredential.user.email,
          displayName: userCredential.user.displayName,
        }),
      );
      log.debug('Auth data stored in AsyncStorage after login');
    } catch (storageError) {
      log.warn('Failed to store auth data in AsyncStorage:', storageError);
      // Continue with login process even if AsyncStorage fails
    }

    return userCredential;
  } catch (error) {
    log.error('Error signing in user:', error);
    throw error;
  }
};

/**
 * Sign out the current user
 */
export const signOutUser = async (): Promise<void> => {
  try {
    log.debug('Attempting to sign out user');
    // Direct usage of auth instance
    await signOut(auth);
    log.debug('User signed out successfully');

    // Clear auth data from AsyncStorage
    try {
      await AsyncStorage.removeItem('authUser');
      log.debug('Auth data cleared from AsyncStorage');
    } catch (storageError) {
      log.warn('Failed to clear auth data from AsyncStorage:', storageError);
      // Continue with sign out process even if AsyncStorage fails
    }
  } catch (error) {
    log.error('Error signing out user:', error);
    throw error;
  }
};

/**
 * Send a password reset email to the specified email address
 * @param email User's email
 */
export const resetPassword = async (email: string): Promise<void> => {
  try {
    log.debug(`Attempting to send password reset email to: ${email}`);
    // Direct usage of auth instance
    await sendPasswordResetEmail(auth, email);
    log.debug('Password reset email sent successfully');
  } catch (error) {
    log.error('Error sending password reset email:', error);
    throw error;
  }
};

/**
 * Gets user data from Firestore, creating a default profile if not exists
 * @param user Firebase user object from auth
 * @returns User data from Firestore or null if error
 */
export const getUserData = async (user: User): Promise<UserProfile | null> => {
  // Safety check on user object
  if (!user || !user.uid) {
    log.error('Invalid user object passed to getUserData:', user);
    return null;
  }

  try {
    log.debug(`Getting user data for uid: ${user.uid}`);
    // Direct usage of firestore instance
    const userDoc = await getDoc(doc(firestore, 'users', user.uid));

    if (userDoc.exists()) {
      log.debug('Found existing user document');
      const data = userDoc.data();
      // Convert the Firestore data to match UserProfile type
      const userProfile: UserProfile = {
        userId: user.uid,
        email: data.email || user.email,
        displayName: data.displayName || user.displayName,
        photoURL: data.photoURL || user.photoURL,
        phoneNumber: data.phoneNumber || user.phoneNumber,
        organizationId: data.organizationId || null,
        role: data.role || 'user',
        createdAt: data.createdAt ? new Date(data.createdAt).getTime() : Date.now(),
        updatedAt: data.updatedAt ? new Date(data.updatedAt).getTime() : Date.now(),
        preferences: data.preferences || {
          theme: 'light',
          language: 'en',
          notifications: true,
        },
      };
      return userProfile;
    } else {
      log.debug('No user document found, creating default profile');
      // Extract username from email or use fallback
      const username = user.email ? user.email.split('@')[0] : `user_${user.uid.substring(0, 6)}`;

      // Create a default profile matching UserProfile type
      const defaultProfile: UserProfile = {
        userId: user.uid,
        email: user.email,
        displayName: user.displayName || username,
        photoURL: user.photoURL,
        phoneNumber: user.phoneNumber,
        role: 'user',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        preferences: {
          theme: 'light',
          language: 'en',
          notifications: true,
        },
      };

      log.debug('Creating default profile:', defaultProfile);

      // Save the default profile to Firestore
      await setDoc(doc(firestore, 'users', user.uid), defaultProfile);

      return defaultProfile;
    }
  } catch (error) {
    log.error('Error getting user data:', error);
    // Return a minimal profile instead of null to avoid crashes
    return {
      userId: user?.uid || 'unknown',
      role: 'user',
    } as UserProfile;
  }
};
